import Calculator from './Calculator.js';

/**
 * BoxCalculator - Calculator for rectangular boxes/planters
 *
 * Formula for box (skrzynia):
 * - x = length (długość) in cm
 * - y = width (szerokość) in cm
 * - z = height (wysokość) in cm
 * - k = drainage thickness = 5cm
 *
 * With drainage:
 * - Substrate volume = ((x*y)*(z-k)) / 1000
 * - Drainage volume = (x*y*k) / 1000
 *
 * Without drainage:
 * - Substrate volume = ((x*y)*z) / 1000
 * - Drainage volume = 0
 *
 * Note: Safety buffer is applied in CalculationEngine, not here
 *
 * @class BoxCalculator
 * @extends Calculator
 */
class BoxCalculator extends Calculator {
    static DRAINAGE_THICKNESS = 5; // cm

    /**
     * @param {Object} dimensions
     * @param {number} dimensions.x - Length in cm
     * @param {number} dimensions.y - Width in cm
     * @param {number} dimensions.z - Height in cm
     * @param {boolean} [withDrainage=true] - Whether to include drainage
     */
    constructor(dimensions, withDrainage = true) {
        super(dimensions);
        const { x = 0, y = 0, z = 0 } = dimensions;

        this.x = parseFloat(x) || 0;
        this.y = parseFloat(y) || 0;
        this.z = parseFloat(z) || 0;
        this.k = BoxCalculator.DRAINAGE_THICKNESS;
        this.withDrainage = withDrainage;
    }

    /**
     * Calculate substrate volume for box
     *
     * @returns {number} Substrate volume in liters
     */
    calculateSubstrateVolume() {
        const baseArea = this.x * this.y;
        const CM3_TO_LITERS = 1000;

        if (this.withDrainage) {
            const effectiveHeight = this.z - this.k;
            return (baseArea * effectiveHeight) / CM3_TO_LITERS;
        }

        return (baseArea * this.z) / CM3_TO_LITERS;
    }

    /**
     * Calculate drainage volume for box (no surplus)
     *
     * @returns {number} Drainage volume in liters
     */
    calculateDrainageVolume() {
        if (!this.withDrainage) {
            return 0;
        }

        const baseArea = this.x * this.y;
        const CM3_TO_LITERS = 1000;

        return (baseArea * this.k) / CM3_TO_LITERS;
    }

    /**
     * Set dimensions
     *
     * @param {Object} dimensions
     */
    setDimensions(dimensions) {
        Object.assign(this.dimensions, dimensions);
        if (dimensions.x !== undefined) this.x = parseFloat(dimensions.x) || 0;
        if (dimensions.y !== undefined) this.y = parseFloat(dimensions.y) || 0;
        if (dimensions.z !== undefined) this.z = parseFloat(dimensions.z) || 0;
        if (dimensions.withDrainage !== undefined) this.withDrainage = dimensions.withDrainage;
    }

    /**
     * Validate dimensions
     *
     * @returns {Object} Validation result
     */
    validate() {
        const errors = [];

        if (this.x <= 0) errors.push({ field: 'x', type: 'required' });
        if (this.y <= 0) errors.push({ field: 'y', type: 'required' });
        if (this.z <= 0) errors.push({ field: 'z', type: 'required' });

        if (this.withDrainage && this.z <= BoxCalculator.DRAINAGE_THICKNESS) {
            errors.push({ field: 'z', type: 'too_small', min: BoxCalculator.DRAINAGE_THICKNESS, unit: 'centimeters' });
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

export default BoxCalculator;